/**
 * Carrousel du bloc « Avis clients ».
 *
 * Le défilement lui-même est natif (scroll-snap) : la souris, le doigt et le
 * clavier fonctionnent même si ce fichier ne se charge pas. Le script n'ajoute
 * que les flèches, les puces, la boucle et le défilement automatique.
 */

const SLIDE = '.onr__slide';
const EDGE = 2; // tolérance en px pour « on est au bout »

class OnReviews extends HTMLElement {
  #timer = null;
  #frame = null;

  connectedCallback() {
    if (this.dataset.onrReady === 'true') return;

    this.track = this.querySelector('[data-onr-track]');
    if (!this.track) return;

    this.dataset.onrReady = 'true';

    this.prevBtn = this.querySelector('[data-onr-prev]');
    this.nextBtn = this.querySelector('[data-onr-next]');
    this.dotsEl = this.querySelector('[data-onr-dots]');
    this.loop = this.dataset.loop === 'true';

    this.handleScroll = () => this.#scheduleSync();
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    this.prevBtn?.addEventListener('click', () => this.step(-1));
    this.nextBtn?.addEventListener('click', () => this.step(1));

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.#scheduleSync());
      this.resizeObserver.observe(this.track);
    }

    this.#buildDots();
    this.#sync();
    this.#setupAutoplay();
    this.#setupEditor();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.stopAutoplay();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    if (this.onSelect) document.removeEventListener('shopify:block:select', this.onSelect);
    if (this.onDeselect) document.removeEventListener('shopify:block:deselect', this.onDeselect);
    delete this.dataset.onrReady;
  }

  get slides() {
    return Array.from(this.track.querySelectorAll(SLIDE));
  }

  /** Position de défilement qui amène `slide` au bord gauche de la piste. */
  #offsetOf(slide) {
    const trackLeft = this.track.getBoundingClientRect().left;
    return slide.getBoundingClientRect().left - trackLeft + this.track.scrollLeft;
  }

  #maxScroll() {
    return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
  }

  /** Index de l'avis actuellement au bord gauche. */
  currentIndex() {
    const slides = this.slides;
    if (!slides.length) return 0;
    const here = this.track.scrollLeft;
    let best = 0;
    let bestGap = Infinity;
    slides.forEach((slide, i) => {
      const gap = Math.abs(this.#offsetOf(slide) - here);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    });
    return best;
  }

  goTo(index) {
    const slides = this.slides;
    if (!slides.length) return;
    const clamped = Math.max(0, Math.min(slides.length - 1, index));
    this.track.scrollTo({ left: this.#offsetOf(slides[clamped]), behavior: 'smooth' });
  }

  step(direction) {
    const slides = this.slides;
    if (slides.length < 2) return;

    const max = this.#maxScroll();
    const atStart = this.track.scrollLeft <= EDGE;
    const atEnd = this.track.scrollLeft >= max - EDGE;

    // Aux extrémités : on boucle, ou on ne fait rien.
    if (direction > 0 && atEnd) {
      if (this.loop) this.goTo(0);
      return;
    }
    if (direction < 0 && atStart) {
      if (this.loop) this.goTo(slides.length - 1);
      return;
    }

    this.goTo(this.currentIndex() + direction);
  }

  #buildDots() {
    if (!this.dotsEl) return;
    const slides = this.slides;
    this.dotsEl.replaceChildren();
    if (slides.length < 2) return;

    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'onr__dot';
      dot.setAttribute('aria-label', `Avis ${i + 1}`);
      dot.addEventListener('click', () => {
        this.stopAutoplay();
        this.goTo(i);
      });
      this.dotsEl.appendChild(dot);
    });
  }

  #scheduleSync() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#sync();
    });
  }

  #sync() {
    if (!this.isConnected || !this.track) return;

    const slides = this.slides;
    const max = this.#maxScroll();
    const nothingToScroll = max <= EDGE;
    const atStart = this.track.scrollLeft <= EDGE;
    const atEnd = this.track.scrollLeft >= max - EDGE;

    // Tout tient à l'écran : pas de flèches, pas de puces.
    if (this.prevBtn) {
      this.prevBtn.hidden = nothingToScroll;
      this.prevBtn.disabled = !this.loop && atStart;
    }
    if (this.nextBtn) {
      this.nextBtn.hidden = nothingToScroll;
      this.nextBtn.disabled = !this.loop && atEnd;
    }
    if (this.dotsEl) {
      this.dotsEl.hidden = nothingToScroll || slides.length < 2;
    }

    const current = this.currentIndex();
    this.dotsEl?.querySelectorAll('.onr__dot').forEach((dot, i) => {
      dot.setAttribute('aria-current', i === current ? 'true' : 'false');
    });
  }

  #setupAutoplay() {
    const seconds = parseFloat(this.dataset.autoplay || '0');
    if (!seconds) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    this.autoplayMs = seconds * 1000;

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());
    this.track.addEventListener('pointerdown', () => this.stopAutoplay(), { passive: true });

    // Ne tourne que si le bloc est visible à l'écran.
    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.startAutoplay() : this.stopAutoplay()),
        { threshold: 0.25 }
      );
      this.intersectionObserver.observe(this);
    } else {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    if (!this.autoplayMs || this.#timer) return;
    this.#timer = setInterval(() => this.step(1), this.autoplayMs);
  }

  stopAutoplay() {
    if (!this.#timer) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  /** Dans l'éditeur de thème : cliquer un avis dans le panneau l'amène à l'écran. */
  #setupEditor() {
    if (!window.Shopify?.designMode) return;

    this.onSelect = (event) => {
      const slide = event.target?.closest?.(SLIDE);
      if (!slide || !this.contains(slide)) return;
      this.stopAutoplay();
      this.goTo(this.slides.indexOf(slide));
    };
    this.onDeselect = () => this.startAutoplay();

    document.addEventListener('shopify:block:select', this.onSelect);
    document.addEventListener('shopify:block:deselect', this.onDeselect);
  }
}

if (!customElements.get('on-reviews')) {
  customElements.define('on-reviews', OnReviews);
}
